/**
 * This module contains all the code that is ran on `window.onload`
 *
 * Add code to this section that should be ran after everything else on the site, i.e not mission critical for the website to function
 * @module globalDelayed
 */

import 'vanilla-ripplejs';
import Headroom from 'headroom.js';
import loadLazyResources from './modules/lazyload/lazyload';
import { addDelegateEventListener } from '@remarkablenet/rcm-utils';
import { setupRippleEffect } from './modules/ripple/ripple';
import { dispatchEventWrapper } from './modules/event/event';
import trackLink from './modules/track-link/track-link';
import * as Header from './modules/update-header/update-header';
import { clearSavedScrollPosition } from './modules/page-loader/utility-functions';
import { isMobile } from '@remarkablenet/rcm-utils';
import { listenEvents } from './modules/event/event';
import tippy from 'tippy.js';
import * as GDPRCheck from './modules/gdpr-check/gdpr-check';
import { login, openLogin } from './modules/login/login';
import { initConsentChecks } from './modules/cookie-consent/cookie-consent';
import { gtag } from "./modules/utility-functions/utility-functions";
import { handleBookAppointmentClick } from './modules/book-appointment-slideout/book-appointment-slideout';
import { isMossMobile } from './modules/mobile-detect/mobile-detect';
import * as HeaderServices from './modules/header-services/header-services';
import { openOffCanvasPanel } from './modules/off-canvas-panel/off-canvas-panel';
import { initCountrySelectorSlideout, initNavCountrySelectorPanel } from './modules/country-selector/country-selector';
import { openPanel, updatePanel } from './modules/slideout-panel/slideout-panel';
import { fetchText } from './modules/fetch-helper/fetch-helper';


/**
 * This is the main callback method that is called on `window.onload`.
 * This method should be kept as small as it can be and have all other code dynamically loaded
 * @name globalDelayed
 * @async
 */


export default async () => {
    console.log('This is the globally delayed code');
    listenEvents();
    initPromoList();
    Header.updateData();
    login();
    initCountrySelectorSlideout();
    initNavCountrySelectorPanel();
    initNavLoginPanel();
    initSelectOpenState('.form-select select');

    // Check for book appointment using delegate event listener
    bindBookAppointmentSlideoutEvent();

  tippy('[data-tippy-content]', {
    arrow: true
  });

    loadLazyResources();
    initConsentChecks();
    setupRippleEffect(document.body, ['.js-ripple', '.btn', 'li a']);

  if (isMobile()) {
        const navItems = document.querySelectorAll('.js-open-menu-panel');

        navItems.forEach((item) => {
            item.addEventListener('click', (e) => {
        const url = item.getAttribute('href') || '';

        const navTitle = item.getAttribute('data-nav-title');
        if (url === '#' && navTitle === 'Country Selector') {
          e.preventDefault();
          return;
        }

                const hasSecondaryLinks = item.nextElementSibling?.querySelector('.nav-secondary-links') ? true : false;

                if (hasSecondaryLinks) return;

                window.location.href = url;
            });
        });
    }

    if (window.innerWidth >= 992) {
        const navItems = document.querySelectorAll<HTMLElement>('.js-open-menu-panel');

        navItems.forEach((item) => {
            item.addEventListener('touchstart', (e) => {
                const openPanels = document.querySelectorAll('.is-open');
                const itemIsActive = item.parentElement.classList.contains('is-open');

                if (itemIsActive) return;

                openPanels.forEach((panel) => panel.classList.remove('is-open'));

                e.preventDefault();

                item.parentElement.classList.add('is-open');
            });

            let hoverTimeout: number | null = null;

            item.addEventListener('mouseenter', () => {
                const wrapper = item.closest('.nav-main-links--has-dropdown') as HTMLElement | null;
                if (!wrapper) return;

                if (wrapper.classList.contains('nav-main-links--no-dropdown')) return;

                const navTitle = item.getAttribute('data-nav-title');
                if (navTitle === 'Country Selector') return;

                const dropdown = wrapper.querySelector<HTMLElement>('.dropdown-menu');
                if (dropdown?.classList.contains('is-active')) return;

                hoverTimeout = window.setTimeout(() => {
                    item.click();
                }, 1000);
            });

            item.addEventListener('mouseleave', () => {
                if (hoverTimeout !== null) {
                    window.clearTimeout(hoverTimeout);
                    hoverTimeout = null;
                }
            });
        });

        document.addEventListener('click', (e) => {
            const target = e.target as HTMLElement | null;

            // Ignore clicks inside the nav drawer so the secondary panel doesn't immediately collapse
            if (target && target.closest('.nav-drawer')) {
                return;
            }

            const nav = document.querySelector('.nav-main-links');
            if (nav) {
                const openNavPanels = nav.querySelectorAll('.is-open') as NodeListOf<HTMLElement>;
                openNavPanels.forEach((panel) => panel.classList.remove('is-open'));
            }
        });
    }

    // Quick Basket
    const quickBasket = document.querySelector('.quick-basket');
    const basketPage = document.body.classList.contains('basketPage');
    if (quickBasket && !isMobile() && !basketPage) {
        import('./modules/quick-basket/quick-basket-ui').then((qb) => {
            const quickBasketInit = qb.init();
            quickBasketInit.loadQuickbasket();
        });
    }

    // Quick Wishlist
    const quickWishlist = document.querySelector('.quick-wishlist');
    if (quickWishlist && !isMobile() && !basketPage) {
        import('./modules/quick-wishlist/quick-wishlist-ui').then((qb) => {
            qb.init();
        });
    }

    // Mobile nav dropdown
    document.querySelectorAll('.nav-quick-links .js-expand-dropdown').forEach((button) => {
        button.querySelector('.mobile-nav-dropdown').addEventListener('click', (e: Event) => {
            e.stopPropagation();
        });

        button.addEventListener('click', (e: Event) => {
            e.preventDefault();
            button.classList.toggle('expanded');
        });
    });

    //Inside pocket carousel
    document.querySelectorAll('.inside-pocket__post').forEach((post: HTMLElement, index) => {
        post.addEventListener('mouseover', () => {
            if (!post.classList.contains('active')) {
                post.parentElement.querySelector('.active')?.classList.remove('active');
                post.classList.add('active');

                (document.querySelector('.inside-pocket__preview img') as HTMLImageElement).src = post.dataset.imgUrl;
            }

            const slideNumber = post.closest('.inside-pocket')?.querySelector('.inside-pocket__slide-number p');
            slideNumber &&
                (slideNumber.innerHTML = `${index + 1} / ${document.querySelectorAll('.inside-pocket__post').length}`);
        });
    });



    let shouldImportSignupUI = true;
    let importedSignUpUI;
    addDelegateEventListener({ container: document, event: 'click', target: '.js-signup-newsletter', callback: (e) => {
        e.originalEvent.preventDefault();
        const delegateTarget = <HTMLElement>e.delegateTarget;
        if (delegateTarget.getAttribute('data-init') != 'true') {
            if (shouldImportSignupUI) {
                import('./modules/signup/signup-ui').then((signupFunc) => {
                    signupFunc.default(delegateTarget);
                    importedSignUpUI = signupFunc;
                    shouldImportSignupUI = false;
                });
            } else {
                importedSignUpUI.default(e.delegateTarget);
            }
            delegateTarget.setAttribute('data-init', 'true');
        }
    } });

    // Form tracking
    const formExists = document.querySelectorAll('form');
    if (formExists) {
        formExists.forEach((form) => {
            form.addEventListener('submit', () => {
                let formClass = form.classList;
                dispatchEventWrapper(document, 'rc-form-submit', {
                    area: 'Form',
                    event: 'Submit',
                    label: formClass,
                    value: 'Success'
                });
            });
        });
    }

    // Track menu links
    const menu = document.getElementById('main-menu');
    if (menu) {
        menu.querySelectorAll('a').forEach((link) => {
            link.addEventListener('click', () => {
                let text = link.innerText.trim();
                if (text !== '') {
                    dispatchEventWrapper(document, 'rc-menu-link-clicked', {
                        area: 'Menu',
                        event: 'clicked',
                        label: text
                    });
                }
            });
        });
    }

    // Track links
    trackLink();

    const loadAccountPanel = () => {
        if (window.location.href.includes('account=load')) {
            openLogin();
        }
    }

    loadAccountPanel(); 


    // Clear saved scroll position if not on a product page
    clearSavedScrollPosition();
    GDPRCheck.init(document.querySelector('#cookieConsent'));
};

const initPromoList = () => {
    const promoListSelector = document.querySelector('.js-cycle-promo-list') as HTMLElement;
    if (!promoListSelector) return;

    const promotionItems = promoListSelector.querySelectorAll('.promo-list__item');

    let promoInterval: number | null = null;
    let index = 0;

    const updateVisibility = () => promotionItems.forEach((elm, i) => elm.classList.toggle('d-none', i !== index));

    const startCycle = () => {
        if (promoInterval) return;

        /* Hide all except first immediately */
        index = 0;
        updateVisibility();

        promoInterval = window.setInterval(() => {
            index = (index + 1) % promotionItems.length;
            updateVisibility();
        }, 2000);
    };

    const stopCycle = () => {
        if (!promoInterval) return;

        clearInterval(promoInterval);
        promoInterval = null;

        /* Show all on desktop */
        promotionItems.forEach(elm => elm.classList.remove('d-none'));
    };

    const evaluate = () => isMossMobile() ? startCycle() : stopCycle();

    // Run on init
    evaluate();

    window.addEventListener('resize', evaluate);

    /* Hover pause */
    promotionItems.forEach(elm => {
        elm.addEventListener('mouseenter', () => {
            if (isMossMobile() && promoInterval) {
                clearInterval(promoInterval);
                promoInterval = null;
            }
        });

        elm.addEventListener('mouseleave', () => {
            if (isMossMobile() && !promoInterval) {
                /* restart the cycle after hover ends */
                promoInterval = window.setInterval(() => {
                    index = (index + 1) % promotionItems.length;
                    promotionItems.forEach((item, i) => item.classList.toggle('d-none', i !== index));
                }, 2000);
            }
        });
    });
};



const setNavLoginView = (root: HTMLElement, view: 'login' | 'register') => {
    const loginPanel = root.querySelector<HTMLElement>('[data-panel="login"]');
    const registerPanel = root.querySelector<HTMLElement>('[data-panel="register"]');
    if (!loginPanel || !registerPanel) return;

    const showRegister = view === 'register';
    loginPanel.classList.toggle('d-none', showRegister);
    registerPanel.classList.toggle('d-none', !showRegister);

    const navSection = root.closest<HTMLElement>('.nav-section');
    const titleEl = navSection?.querySelector<HTMLElement>('.login-panel-title');
    if (titleEl) {
        titleEl.textContent = showRegister ? 'Sign Up' : 'Log In';
    }
};

const initNavLoginToggles = (root: HTMLElement) => {
    const anyRoot = root as any;
    if (anyRoot._navLoginInit) return;
    anyRoot._navLoginInit = true;

    const showRegisterLink = root.querySelector<HTMLElement>('.js-show-register');
    if (showRegisterLink) {
        showRegisterLink.addEventListener('click', (e) => {
            e.preventDefault();
            setNavLoginView(root, 'register');
        });
    }

    const showLoginLink = root.querySelector<HTMLElement>('.js-show-login');
    if (showLoginLink) {
        showLoginLink.addEventListener('click', (e) => {
            e.preventDefault();
            setNavLoginView(root, 'login');
        });
    }
};

const initNavLoginPanel = () => {
    const navLoginTriggers = document.querySelectorAll<HTMLElement>('.nav-drawer .js-login-register[data-nav-origin="nav"]');
    if (!navLoginTriggers.length) return;

    let loadedDesktop = false;
    let loadedMobile = false;

    const loadInto = (target: HTMLElement): Promise<void> => {
        if (loadedDesktop || loadedMobile) return Promise.resolve();

        loadedDesktop = true;
        loadedMobile = true;

        const url = `/account`;
        const data = fetchText(url, null);

        return data.then(async (markup) => {
            target.innerHTML = markup;

            try {
                const { initLoginForms } = await import('./modules/login/login');
                initLoginForms(undefined, target.closest('.js-nav-login-panel') as ParentNode | null || undefined);
            } catch {
            }
        });
    };

    navLoginTriggers.forEach(trigger => {
        trigger.addEventListener('click', async (e) => {
            e.preventDefault();
            e.stopPropagation();

            const session = await HeaderServices.getData();
            if (session.isLoggedIn) {
                window.location.href = '/account/manage';
                return;
            }

            const navDrawer = document.querySelector('.nav-drawer') as HTMLElement | null;
            if (!navDrawer) return;

            const desktopPanel = navDrawer.querySelector<HTMLElement>('.js-nav-login-panel');
            const action = (trigger.dataset.navAction as 'login' | 'register') || 'login';

            if (desktopPanel) {
                const target = desktopPanel.querySelector<HTMLElement>('.js-nav-login');
                if (!target) return;

                loadInto(target).then(() => {
                    initNavLoginToggles(target);
                    setNavLoginView(target, action);

                    const anyActiveDropdown = navDrawer.querySelector<HTMLElement>('.off-canvas-panel.is-active');

                    if (!anyActiveDropdown) {
                        openOffCanvasPanel(desktopPanel);
                    } else if (anyActiveDropdown === desktopPanel) {
                        desktopPanel.classList.add('is-active', 'is-visible');
                    } else {
                        desktopPanel.classList.add('is-active', 'is-visible');
                        anyActiveDropdown.classList.remove('is-active');
                        navDrawer.classList.add('secondary-open');
                    }
                });
            }
        });
    });
};

/**
 * This function binds the a click event to the class `.js-open-book-appointment-slideout`.
 * It is used to open the book appointment slideout panel when the user clicks on the button.
 * It prevents the default action and opens the appointment slideout panel.
 * @remarks Call this function only once after the DOM is fully loaded, and on global delayed.
 * @returns {void}
 */
const bindBookAppointmentSlideoutEvent = (): void => {
    addDelegateEventListener({ container: document, event: 'click', target: '.js-open-book-appointment-slideout', callback: async (e) => {
        e.originalEvent.preventDefault();
        const target = e.delegateTarget as HTMLElement;
        handleBookAppointmentClick(target, false);
    } });
};

const initSelectOpenState = (selector: string): void => {
    const sel = document.querySelector<HTMLSelectElement>(selector);
    if (!sel) return;

    const group = sel.closest<HTMLElement>('.form-group') ?? sel.parentElement as HTMLElement;
    const OPEN_CLASS = 'is-active';
    const open = () => group.classList.add(OPEN_CLASS);
    const close = () => group.classList.remove(OPEN_CLASS);
    const isOpen = () => group.classList.contains(OPEN_CLASS);

    sel.addEventListener('pointerdown', () => {
        if (isOpen()) close();
        else open();
    });

    sel.addEventListener('change', close);

    sel.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === 'Escape') close();
        else if (e.key === ' ' || e.key === 'Enter' || e.key === 'ArrowDown' || e.key === 'ArrowUp' || (e.altKey && e.key === 'ArrowDown')) open();
    });

    document.addEventListener('pointerdown', (e: PointerEvent) => {
        const t = e.target as Node | null;
        if (t && !group.contains(t)) close();
    }, true);
};